import { ChangeDetectorRef, Component, EventEmitter, inject, Input, OnChanges, Output, signal, SimpleChanges } from '@angular/core';
import { AgGridModule } from 'ag-grid-angular';
import { IClaim } from '../../../shared/interfaces/claims.model';
import { ClaimsService } from '../../../core/services/claims.service';
import { ColDef, GridApi } from 'ag-grid-enterprise';
import { ExcelExportParams, GridOptions, GridReadyEvent, ProcessCellForExportParams } from 'ag-grid-community';
import { ClaimStore } from '../../../core/store/claim-store';
import { GridService } from '../../../core/services/grid.service';
import { AG_GRID_INPUTS } from '../../../shared/constants/ag-grid-inputs.constants';
import { MatDialog } from '@angular/material/dialog';
import { AddAssertionDialogComponent } from '../../../shared/components/add-assertion/add-assertion-dialog.component';
import { ViewAssertionsDialogComponent } from '../../../shared/components/view-assertions-dialog/view-assertions-dialog.component';
import { IAssertionDialogData } from '../../../shared/interfaces/add-assertion.interface';
import { UserGeography } from '../../../shared/models/user-geography.model';
import { Geography } from '../../../shared/models/geography.model';
import { IViewAssertionRowData } from '../../../shared/interfaces/view-assertion-rowdata.interface';
import { SnackBarService } from '../../../core/services/snack-bar.service';
import { GenericLoaderData } from '../../../shared/models/generic-loader.model';
import { CLAIMS_THRESHOLDS_VALUES, CLASS_NAME, CUSTOM_EVENT, MENU_ICONS, SOURCE_TYPE_ID } from '../../../shared/constants/claims-ui-constants';
import { CLAIMS_GRID_MESSAGES, FILENAME_FORMAT } from '../../../shared/constants/claims-grid-messages.constants';
import { CopyPasteExportService } from '../../../core/services/copy-paste-export.service';
import { GenericLoaderComponent } from '../../../shared/components/generic-loader/generic-loader.component';

@Component({
  selector: 'claims-main-grid',
  imports: [AgGridModule, GenericLoaderComponent],
  standalone: true,
  templateUrl: './main-grid.component.html',
  styleUrl: './main-grid.component.scss',
})
export class MainGridComponent implements OnChanges {
  @Input() public userGeographies: Array<UserGeography> = [];
  @Input() public projectGeographies: Array<Geography> = [];
  public rowData: Array<IClaim> = [];
  public colDefs: Array<ColDef<IClaim>> = [];
  public totalRecords = 0;
  private readonly dialog = inject(MatDialog);
  public isLoading = signal<boolean>(false);
  public readonly gridProperties = AG_GRID_INPUTS;
  private gridApi!: GridApi;
  @Output() public selectionChange = new EventEmitter<boolean>();
  @Output() public rowCountChange = new EventEmitter<number>();
  public genericLoaderData: GenericLoaderData = new GenericLoaderData();

  public exportModalOpen = false;
  private readonly previousActiveElement: HTMLElement | null = null;
  public isExportToExcelEnabled = false;
  public closeExportModal(): void {
    this.exportModalOpen = false;
    document.body.style.overflow = '';
    if (this.previousActiveElement) {
      this.previousActiveElement.focus();
    }
  }

  public onModalKeydown(event: KeyboardEvent): void {
    if (event.key === 'Tab') {
      const modal = document.getElementById('export-modal');
      if (!modal) return;
      const focusable = modal.querySelectorAll<HTMLElement>('[tabindex]:not([tabindex="-1"])');
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey) {
        if (document.activeElement === first) {
          event.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    } else if (event.key === 'Escape') {
      this.closeExportModal();
    }
  }

  public gridOptions: GridOptions = {
    suppressRowTransform: true,
    animateRows: true,
    suppressRowClickSelection: true,
    rowSelection: 'multiple',
    rowModelType: 'clientSide',
    getRowHeight: (params) => undefined,
    defaultColDef: {
      filter: false
    },
    getContextMenuItems: (params: any) => {
      const rowData = params?.node?.data ?? (params.api.getSelectedRows?.() ?? [])[0] ?? null;
      return [
        {
          name: 'Assertions',
          icon: MENU_ICONS.ASSERTIONS_REGULAR,
          subMenu: [
            {
              name: 'Add Assertions',
              icon:  MENU_ICONS.ASSERTIONS_REGULAR,
              action: () => rowData && this.onAddAssertions(rowData)
            },
            {
              name: 'View Assertions',
              icon: MENU_ICONS.ASSERTIONS_SOLID,
              action: () => rowData && this.onViewAssertions(rowData)
            }
          ]
        },
        {
          name: 'Export',
          icon: MENU_ICONS.EXPORT,
          action: () => {
            const eventName = CUSTOM_EVENT.claimsShowHideExportModalDialog;
            document.dispatchEvent(
              new CustomEvent(eventName, {
                bubbles: true,
                cancelable: true,
                detail: null
              })
            );
          }
        }
      ];
    },
  };

  constructor(
    private readonly gridService: GridService,
    public readonly claimsServe: ClaimsService,
    private readonly store: ClaimStore,
    private readonly cdr: ChangeDetectorRef,
    private readonly snackBarService: SnackBarService,
    private readonly copyPasteExportService: CopyPasteExportService
  ) {
    if (this.gridOptions.context == null) {
      this.gridOptions.context = { componentParent: this };
    }
    document.addEventListener('claims.load-grid-data', () => {
      this.loadClaims();
    });
  }

  public onAddAssertions(rowData: any) {
    const dialogData: IAssertionDialogData = {
      mode: 'add',
      claimData: rowData,
      userGeographies: this.userGeographies,
      projectGeographies: this.projectGeographies
    };

    this.dialog.open(AddAssertionDialogComponent, {
      data: dialogData,
      width: '1200px',
      maxHeight: '90vh'
    }).afterClosed().subscribe((result: any) => {
      if (result && result.action === 'save') {
        this.loadClaims();
      }
    });
  }

  public onViewAssertions(rowData: any) {
    const dialogData : IViewAssertionRowData = {
      claimId: rowData?.claimId,
      assertions: rowData?.assertions || []
    };

    this.dialog.open(ViewAssertionsDialogComponent, {
      data: dialogData,
      width: '1200px',
      maxHeight: '90vh',
      panelClass: 'view-assertions-dialog-panel'
    });
  }


  public onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    params.api.sizeColumnsToFit();

    params.api.addEventListener('cellClicked', (event: any) => {
      const crmLink = event.event.target as HTMLElement;
      if (crmLink.classList.contains('crm-link')) {
        const accountId = crmLink.dataset['id'];
        if (accountId) {
          this.openSalesforceLink(accountId);
        }
      }
    });

  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (this.gridApi) {
      this.loadClaims();
    }
  }

  public onSelectionChanged(event: any): void {
    const selectedRows = event.api?.getSelectedRows?.() ?? [];
    this.store.selectedClaims.set(selectedRows.length > 0 ? selectedRows : []);
    this.selectionChange.emit(selectedRows.length > 0);
  }

  public loadClaims(): void {
    const claimsRequest = this.gridService.buildClaimsRequest();
    this.isLoading.set(true);
  
    this.store.selectedClaims.set([]);
    this.selectionChange.emit(false);
    
    this.claimsServe.getClaims(claimsRequest).subscribe({
      next: (claims: Array<IClaim>) => {
        this.store.claims.set(claims);
        if (this.store.claims() != null) {
          const allClaims = (this.store.claims() ?? []).sort((a, b) =>
            new Date(b.expiryDate).getTime() - new Date(a.expiryDate).getTime()
          );
          this.rowData = allClaims;
          this.colDefs = this.gridService.getClaimsGridHeaders(allClaims);
          this.totalRecords = allClaims.length;
          this.rowCountChange.emit(this.totalRecords);
        }
        this.isLoading.set(false);
        if (this.gridApi) {
          this.gridApi.deselectAll();
        }
      },
      error: (err) => {
        this.isLoading.set(false);
      },
    });
  }
  public controlBarRef: any;
  private openSalesforceLink(accountId: string) {
    this.claimsServe.getCRMLink(SOURCE_TYPE_ID).subscribe((link: string) => {

    if (link) {
      const finalUrl = link.replace('[#ID#]', accountId);
      window.open(finalUrl, '_blank');
    }
  });
  }

  public hasSelectedRows(): boolean {
    if (!this.gridApi) {
      return false;
    }
    return this.gridApi.getSelectedNodes().length > 0;
  }

  public getSelectedClaims(): Array<IClaim> {
    if (!this.gridApi) {
      return [];
    }
    
    const selectedNodes = this.gridApi.getSelectedNodes();
    return selectedNodes.map((node: any) => node.data);
  }

  public deleteSelectedRows(): void {
    if (!this.gridApi) {
      return;
    }

    const selectedNodes = this.gridApi.getSelectedNodes();
    const selectedData: Array<IClaim> = selectedNodes.map((node: any) => node.data);

    this.rowData = this.rowData.filter(row => 
      !selectedData.some(selected => 
        row.claimId === selected.claimId
      )
    );

    this.gridApi.setRowData(this.rowData);
    this.gridApi.deselectAll();
    this.store.claims.set(this.rowData);
    this.totalRecords = this.rowData.length;    
    this.selectionChange.emit(false);
    this.cdr.detectChanges();
  }

  public onExport(fileName: string): void {
    try {
      this.showHideUiBlockPop(true, CLAIMS_GRID_MESSAGES.exportToExcelPleaseWait);
      setTimeout(() => {
        this.isExportToExcelEnabled = true;
        this.gridOptions?.api?.exportDataAsExcel({
          fileName,
          sheetName: FILENAME_FORMAT.exportToExcelSheetName,
          processCellCallback: (params: ProcessCellForExportParams) =>
            this.copyPasteExportService.getCellValueForClipboard(params, this.isExportToExcelEnabled)
        } as ExcelExportParams);
        this.showHideUiBlockPop(false, '');
        this.snackBarService.showSnackBar(
          CLAIMS_GRID_MESSAGES.exportToExcelSuccessfully.replace('{fileName}', fileName),
          CLAIMS_THRESHOLDS_VALUES.showExportMessagesForTimeDurationInMilliSeconds,
          CLASS_NAME.success
        );
        this.isExportToExcelEnabled = false;
      }, CLAIMS_THRESHOLDS_VALUES.waitForExportToExcelMilliSeconds);
    } 
    catch (error) {
      this.showHideUiBlockPop(false, '');
      this.snackBarService.showSnackBar(
        CLAIMS_GRID_MESSAGES.exportToExcelSystemErrorsMessage,
        CLAIMS_THRESHOLDS_VALUES.showExportMessagesForTimeDurationInMilliSeconds,
        CLASS_NAME.errors
      );
    }
  }

  public showHideUiBlockPop(isShowHide: boolean, message?: string): void {
    this.genericLoaderData = {
      showLoader: isShowHide,
      loaderMessage: message ?? ''
    };
  }
}
